import type { ContextVocabularyFile, IssueItem, VocabularyEntry, VocabularyOrigin } from '../types/daily'

export interface TextLookupSelection {
  term: string
  normalized: string
  contextExcerpt: string
  anchor: { left: number; top: number; bottom: number }
}

interface DictionaryManifest {
  schemaVersion: '1.0.0'
  dictionaryVersion: string
  format: { bucketCount: number; hash: 'fnv1a32-utf8' }
}

interface DictionaryEntryRecord {
  h: string
  l: string
  p?: string
  t: string[]
  pos?: string[]
}

interface DictionaryAliasRecord { a: string }
type DictionaryRecord = DictionaryEntryRecord | DictionaryAliasRecord
interface DictionaryBucket { v: '1.0.0'; r: Record<string, DictionaryRecord> }

export interface ResolvedVocabularyLookup {
  status: 'resolved'
  vocabulary: VocabularyEntry
  origin: Exclude<VocabularyOrigin, 'pending'>
  normalizedTerm: string
  dictionaryVersion?: string
}

export interface MissingVocabularyLookup {
  status: 'offline' | 'not_found'
}

export type VocabularyLookupResult = ResolvedVocabularyLookup | MissingVocabularyLookup

const manifestUrl = '/data/dictionary/v1/manifest.json'
let manifestPromise: Promise<DictionaryManifest> | undefined
const bucketPromises = new Map<number, Promise<DictionaryBucket>>()
const contextPromises = new Map<string, Promise<ContextVocabularyFile | undefined>>()

export const normalizeLookupText = (value: string) => value
  .normalize('NFKC')
  .replaceAll('’', "'")
  .replaceAll('–', '-')
  .replaceAll('—', '-')
  .trim()
  .replace(/^[\[\]{}()<>“”"'.,:;!?]+|[\[\]{}()<>“”"'.,:;!?]+$/g, '')
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('en')

export const isLookupSelection = (value: string) => {
  const normalized = normalizeLookupText(value)
  if (!normalized || normalized.length > 80) return false
  const words = normalized.split(' ')
  return words.length <= 6 && words.every((word) => /^[a-z]+(?:['-][a-z]+)*$/.test(word))
}

const fnv1a = (value: string) => {
  let result = 0x811c9dc5
  for (const byte of new TextEncoder().encode(value)) {
    result ^= byte
    result = Math.imul(result, 0x01000193) >>> 0
  }
  return result
}

const loadManifest = () => {
  manifestPromise ??= fetch(manifestUrl).then(async (response) => {
    if (!response.ok) throw new Error(`dictionary_manifest_${response.status}`)
    return response.json() as Promise<DictionaryManifest>
  })
  return manifestPromise
}

const loadBucket = async (index: number) => {
  const existing = bucketPromises.get(index)
  if (existing) return existing
  const promise = fetch(`/data/dictionary/v1/bucket-${String(index).padStart(3, '0')}.json`).then(async (response) => {
    if (!response.ok) throw new Error(`dictionary_bucket_${response.status}`)
    return response.json() as Promise<DictionaryBucket>
  })
  bucketPromises.set(index, promise)
  return promise
}

const loadDictionaryRecord = async (term: string, depth = 0): Promise<{ manifest: DictionaryManifest; entry?: DictionaryEntryRecord }> => {
  const manifest = await loadManifest()
  const index = fnv1a(term) % manifest.format.bucketCount
  const record = (await loadBucket(index)).r[term]
  if (!record) return { manifest }
  if ('a' in record) {
    if (depth > 1 || record.a === term) return { manifest }
    return loadDictionaryRecord(record.a, depth + 1)
  }
  return { manifest, entry: record }
}

const isSingleLookupWord = (value: string) => /^[a-z]+(?:['-][a-z]+)*$/.test(value)

const loadDictionaryDefinition = async (term: string) => {
  const result = await loadDictionaryRecord(term)
  let phonetic = result.entry?.p
  const lemma = result.entry?.l
  if (!phonetic && lemma && lemma !== term && isSingleLookupWord(lemma)) {
    phonetic = (await loadDictionaryRecord(lemma)).entry?.p
  }
  return { ...result, phonetic }
}

const decodeDictionaryLineBreaks = (value: string) => value
  .replaceAll('\\r\\n', '\n')
  .replaceAll('\\n', '\n')
  .replaceAll('\\r', '\n')

const dictionaryMeaning = (entry: DictionaryEntryRecord) => entry.t
  .map(decodeDictionaryLineBreaks)
  .join('\n')

const enrichSingleWordPhonetic = async (
  vocabulary: VocabularyEntry,
  normalized: string,
): Promise<{ vocabulary: VocabularyEntry; dictionaryVersion?: string }> => {
  if (vocabulary.phonetic || !isSingleLookupWord(normalized)) return { vocabulary }
  try {
    const { manifest, entry, phonetic } = await loadDictionaryDefinition(normalized)
    if (!entry || !phonetic) return { vocabulary }
    return {
      vocabulary: { ...vocabulary, lemma: vocabulary.lemma ?? entry.l, phonetic },
      dictionaryVersion: manifest.dictionaryVersion,
    }
  } catch {
    // A missing optional phonetic must not hide a curated or contextual definition.
    return { vocabulary }
  }
}

const contextUrl = (issueDate: string) => `/data/vocabulary/daily/${issueDate}.json`

export const loadContextVocabulary = (issueDate: string) => {
  const existing = contextPromises.get(issueDate)
  if (existing) return existing
  const promise = fetch(contextUrl(issueDate)).then(async (response) => {
    if (response.status === 404) return undefined
    if (!response.ok) throw new Error(`context_vocabulary_${response.status}`)
    return response.json() as Promise<ContextVocabularyFile>
  })
  contextPromises.set(issueDate, promise)
  return promise
}

export const prefetchContextVocabulary = async (issueDate: string) => {
  try {
    await loadContextVocabulary(issueDate)
  } catch {
    // Optional enrichment must never block reading or issue caching.
  }
}

const matchesEntry = (entry: VocabularyEntry, normalized: string) => [entry.term, ...(entry.aliases ?? [])]
  .some((value) => normalizeLookupText(value) === normalized)

const resolved = (
  vocabulary: VocabularyEntry,
  origin: ResolvedVocabularyLookup['origin'],
  dictionaryVersion?: string,
): ResolvedVocabularyLookup => ({
  status: 'resolved',
  vocabulary,
  origin,
  normalizedTerm: normalizeLookupText(vocabulary.lemma ?? vocabulary.term),
  dictionaryVersion,
})

export async function lookupVocabulary(
  issueDate: string,
  item: IssueItem,
  selection: Pick<TextLookupSelection, 'term' | 'normalized'>,
): Promise<VocabularyLookupResult> {
  const curated = item.languageAid?.vocabulary.find((entry) => matchesEntry(entry, selection.normalized))
  if (curated) {
    const enriched = await enrichSingleWordPhonetic(curated, selection.normalized)
    return resolved(enriched.vocabulary, 'curated', enriched.dictionaryVersion)
  }

  try {
    const context = await loadContextVocabulary(issueDate)
    const contextual = context?.items.find((entry) => entry.itemId === item.id)?.entries
      .find((entry) => matchesEntry(entry, selection.normalized))
    if (contextual) {
      const enriched = await enrichSingleWordPhonetic(contextual, selection.normalized)
      return resolved(enriched.vocabulary, 'contextual', enriched.dictionaryVersion)
    }
  } catch {
    // The general dictionary remains usable when optional issue enrichment fails.
  }

  try {
    const { manifest, entry, phonetic } = await loadDictionaryDefinition(selection.normalized)
    if (!entry) return { status: 'not_found' }
    return resolved({
      term: selection.term,
      lemma: entry.l,
      phonetic,
      partOfSpeech: entry.pos?.join(' / '),
      difficulty: 'unrated',
      meaningZh: dictionaryMeaning(entry),
      meaningInContextZh: '这是通用词典释义，请结合上方原句判断本文中的具体含义。',
      collocation: '',
    }, 'dictionary', manifest.dictionaryVersion)
  } catch {
    return { status: 'offline' }
  }
}

export async function lookupGeneralDictionary(term: string): Promise<VocabularyLookupResult> {
  const normalized = normalizeLookupText(term)
  try {
    const { manifest, entry, phonetic } = await loadDictionaryDefinition(normalized)
    if (!entry) return { status: 'not_found' }
    return resolved({
      term,
      lemma: entry.l,
      phonetic,
      partOfSpeech: entry.pos?.join(' / '),
      difficulty: 'unrated',
      meaningZh: dictionaryMeaning(entry),
      meaningInContextZh: '这是通用词典释义，请结合保存的原句判断具体含义。',
      collocation: '',
    }, 'dictionary', manifest.dictionaryVersion)
  } catch {
    return { status: 'offline' }
  }
}

export function clearVocabularyLookupCachesForTests() {
  manifestPromise = undefined
  bucketPromises.clear()
  contextPromises.clear()
}
